import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Loader2, CheckCircle, AlertCircle } from 'lucide-react';

export default function AuthCallback() {
  const { handleOAuthCallback } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const hasProcessed = useRef(false);
  const [status, setStatus] = useState('processing'); // processing, success, error
  const [errorMsg, setErrorMsg] = useState('');

  const processCallback = useCallback(async () => {
    try {
      // Extract session_id from URL fragment
      const hash = location.hash;
      const params = new URLSearchParams(hash.replace('#', ''));
      const sessionId = params.get('session_id');

      if (!sessionId) {
        console.error('No session_id found in URL');
        setStatus('error');
        setErrorMsg('Authentication failed. Please try again.');
        setTimeout(() => navigate('/auth'), 1500);
        return;
      }

      // Exchange session_id for user data
      const userData = await handleOAuthCallback(sessionId);
      
      setStatus('success');
      
      // Instant redirect to dashboard - no delay
      navigate('/dashboard', { state: { user: userData }, replace: true });
    } catch (error) {
      console.error('OAuth callback error:', error);
      setStatus('error');
      setErrorMsg('Sign in failed. Please try again.');
      setTimeout(() => navigate('/auth'), 1500);
    }
  }, [location.hash, handleOAuthCallback, navigate]);

  useEffect(() => {
    // Prevent double processing in StrictMode
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    processCallback();
  }, [processCallback]);

  return (
    <div className="min-h-screen bg-[var(--bg-primary)] flex items-center justify-center">
      <div className="text-center p-8 rounded-2xl bg-[var(--bg-card)] border border-[var(--border-primary)] max-w-sm w-full mx-4">
        {status === 'processing' && (
          <>
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-[var(--color-primary)]/10 flex items-center justify-center">
              <Loader2 className="w-8 h-8 animate-spin text-[var(--color-primary)]" />
            </div>
            <h2 className="text-xl font-semibold text-[var(--text-primary)] mb-2">Signing you in...</h2>
            <p className="text-[var(--text-secondary)]">Please wait a moment</p>
          </>
        )}
        
        {status === 'success' && (
          <>
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-green-500/10 flex items-center justify-center">
              <CheckCircle className="w-8 h-8 text-green-500" />
            </div>
            <h2 className="text-xl font-semibold text-[var(--text-primary)] mb-2">Welcome!</h2>
            <p className="text-[var(--text-secondary)]">Redirecting to your dashboard...</p>
          </>
        )}
        
        {status === 'error' && (
          <>
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-500/10 flex items-center justify-center">
              <AlertCircle className="w-8 h-8 text-red-500" />
            </div>
            <h2 className="text-xl font-semibold text-[var(--text-primary)] mb-2">Oops!</h2>
            <p className="text-[var(--text-secondary)]">{errorMsg}</p>
          </>
        )}
      </div>
    </div>
  );
}
